import { isRecord } from '../mappers';
import { type AuditMessage, type AuditText, auditMessage } from '../text';
import type { AuditLogEntry } from '../types';

import { listLength, textField } from './lines';
import type { AuditSummary } from './types';

/** Client access as `user.client_access.*` and invites record it: `{role, locationIds}`. */
interface AccessState {
    role: AuditText;
    /** Null when the entry did not record a site list. */
    siteCount: number | null;
}

function accessState(value: unknown): AccessState | null {
    if (!isRecord(value)) return null;
    return {
        role: textField(value, 'role') ?? auditMessage('auditLog.summary.value.empty'),
        siteCount: listLength(value, 'locationIds'),
    };
}

/** An empty site list is full client access, which the write path stores for ADMIN. */
function withSites(
    base: string,
    values: Record<string, AuditText>,
    state: AccessState | null
): AuditMessage {
    if (state?.siteCount === 0) return auditMessage(`${base}AllSites`, values);
    if (typeof state?.siteCount === 'number') {
        return auditMessage(`${base}Sites`, { ...values, count: state.siteCount });
    }
    return auditMessage(base, values);
}

function summary(rule: 'access' | 'invite', line: AuditMessage): AuditSummary {
    return { rule, line, title: [line] };
}

/** Rule 2: a grant, change or revoke of a user's client access. */
export function summarizeAccess(entry: AuditLogEntry): AuditSummary | null {
    const changes = entry.changes;
    if (changes === null) return null;
    const before = accessState(changes.before);
    if (entry.action === 'user.client_access.revoke') {
        return summary(
            'access',
            before === null
                ? auditMessage('auditLog.summary.accessRevokedNone')
                : withSites('auditLog.summary.accessRevoked', { role: before.role }, before)
        );
    }
    if (entry.action !== 'user.client_access.set') return null;
    const after = accessState(changes.after);
    if (after === null) return null;
    return summary(
        'access',
        before === null
            ? withSites('auditLog.summary.accessGranted', { role: after.role }, after)
            : withSites(
                  'auditLog.summary.accessChanged',
                  { from: before.role, to: after.role },
                  after
              )
    );
}

/**
 * Invites record the resulting access as a diff too, so they are summarized before the
 * generic diff rule would list `role` and `locationIds` as raw fields.
 */
export function summarizeInvite(entry: AuditLogEntry): AuditSummary | null {
    if (entry.action !== 'user.invite' && entry.action !== 'user.reinvite') return null;
    const after = entry.changes === null ? null : accessState(entry.changes.after);
    const invitedRole = entry.metadata === null ? null : textField(entry.metadata, 'role');
    const role = invitedRole ?? after?.role;
    if (role === undefined) return null;
    return summary('invite', withSites('auditLog.summary.invitedAs', { role }, after));
}
